

import React, { useState } from 'react';
import type { GridColumn, FilterGroup, FilterCondition, FilterOperator, DateRangeFilter } from '../types/gridTypes';
import { getOperatorsForType } from '../utils/filterUtils';
import { IconPlus, IconTrash, IconX, IconFilter, IconEye } from './icons';
import { DateRangeFilterPanel } from './DateRangeFilterPanel';
import { DateRangeChip } from './DateRangeChip';

interface Props {
  columns: GridColumn[];
  initialGroup: FilterGroup | undefined;
  dateRangeFilter: DateRangeFilter | null;
  onApply: (group: FilterGroup | undefined) => void;
  onDateRangeFilterChange: (filter: DateRangeFilter | null) => void;
  onToggleColumnVisibility: (colId: string) => void;
  onClose: () => void;
}

export const FilterModal: React.FC<Props> = ({
  columns,
  initialGroup,
  dateRangeFilter,
  onApply,
  onDateRangeFilterChange,
  onToggleColumnVisibility,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'rows' | 'columns'>('rows');
  const [conditions, setConditions] = useState<FilterCondition[]>(
    initialGroup?.conditions || []
  );

  const addCondition = () => {
    const firstCol = columns[0];
    if (!firstCol) return;
    
    const newCond: FilterCondition = {
      id: Math.random().toString(36).substr(2, 9),
      columnId: firstCol.id,
      operator: 'contains', // Default
      value: ''
    };
    setConditions([...conditions, newCond]);
  };

  const updateCondition = (id: string, field: keyof FilterCondition, value: any) => {
    setConditions(prev => prev.map(c => {
      if (c.id !== id) return c;
      
      // Reset operator and value if column changes
      if (field === 'columnId') {
         const newCol = columns.find(col => col.id === value);
         if (newCol) {
            const ops = getOperatorsForType(newCol.type);
            return { ...c, columnId: value, operator: ops[0].value as FilterOperator, value: '' };
         }
      }
      
      return { ...c, [field]: value };
    }));
  };

  const removeCondition = (id: string) => {
    setConditions(prev => prev.filter(c => c.id !== id));
  };

  const handleApply = () => {
    if (conditions.length === 0) {
      onApply(undefined);
    } else {
      onApply({ id: 'g1', conditions, logic: 'AND' });
    }
    onClose();
  };

  return (
    <div className="modal-overlay fixed inset-0 flex items-center justify-center backdrop-blur-sm">
       <div className="modal-container rounded-lg shadow-2xl w-[500px] max-w-[90vw] flex flex-col max-h-[80vh]">
          <div className="modal-header flex items-center justify-between p-4 border-b">
             <h3 className="modal-title font-bold">Filter & Visibility</h3>
             <button onClick={onClose} className="modal-close-btn"><IconX className="w-5 h-5" /></button>
          </div>

          <div className="modal-tabs flex items-center border-b">
             <button
                onClick={() => setActiveTab('rows')}
                className={`modal-tab flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'rows' ? 'active' : ''}`}
             >
                 <IconFilter className="w-4 h-4" />
                 Row Filters
             </button>
             <button
                onClick={() => setActiveTab('columns')}
                className={`modal-tab flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'columns' ? 'active' : ''}`}
             >
                 <IconEye className="w-4 h-4" />
                 Columns
             </button>
          </div>

          <div className="modal-content p-4 overflow-y-auto flex-1">
             {activeTab === 'rows' ? (
                 <>
                    {/* Active date range chip (AC-4.3.4) */}
                    {dateRangeFilter && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        <DateRangeChip
                          filter={dateRangeFilter}
                          columns={columns}
                          onRemove={() => onDateRangeFilterChange(null)}
                        />
                      </div>
                    )}

                    {conditions.length === 0 && !dateRangeFilter ? (
                        <div className="modal-empty-state text-center py-8 italic">
                            No filters applied. Rows match all conditions.
                        </div>
                    ) : conditions.length === 0 ? (
                        <div className="modal-empty-state text-center py-4 italic text-sm">
                            No condition filters. Add conditions below or use date range filter.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {conditions.map((cond, idx) => {
                                const col = columns.find(c => c.id === cond.columnId);
                                const operators = col ? getOperatorsForType(col.type) : [];

                                return (
                                    <div key={cond.id} className="modal-filter-row flex items-center gap-2 p-2 rounded border">
                                        <div className="modal-filter-label text-xs font-bold w-8 text-center">
                                            {idx === 0 ? 'Where' : 'And'}
                                        </div>
                                        <select
                                            className="modal-select border rounded px-2 py-1 text-sm max-w-[120px]"
                                            value={cond.columnId}
                                            onChange={(e) => updateCondition(cond.id, 'columnId', e.target.value)}
                                        >
                                            {columns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                                        </select>

                                        <select
                                            className="modal-select border rounded px-2 py-1 text-sm max-w-[120px]"
                                            value={cond.operator}
                                            onChange={(e) => updateCondition(cond.id, 'operator', e.target.value)}
                                        >
                                            {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                                        </select>

                                        {/* Value Input based on Operator/Type */}
                                        {cond.operator !== 'is_empty' && cond.operator !== 'is_not_empty' && (
                                            col?.type === 'date' ? (
                                                <input
                                                    type="date"
                                                    className="modal-input flex-1 border rounded px-2 py-1 text-sm min-w-[100px]"
                                                    value={cond.value}
                                                    onChange={(e) => updateCondition(cond.id, 'value', e.target.value)}
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    className="modal-input flex-1 border rounded px-2 py-1 text-sm min-w-[100px]"
                                                    placeholder="Value"
                                                    value={cond.value}
                                                    onChange={(e) => updateCondition(cond.id, 'value', e.target.value)}
                                                />
                                            )
                                        )}

                                        <button onClick={() => removeCondition(cond.id)} className="modal-delete-btn p-1">
                                            <IconTrash className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <button onClick={addCondition} className="modal-add-btn mt-4 flex items-center gap-2 text-sm font-medium">
                        <IconPlus className="w-4 h-4" />
                        Add condition
                    </button>

                    {/* Date Range Filter Panel (AC-4.3.1) */}
                    <DateRangeFilterPanel
                      columns={columns}
                      filter={dateRangeFilter}
                      onFilterChange={onDateRangeFilterChange}
                    />
                 </>
             ) : (
                 <div className="space-y-2">
                     <p className="modal-hint text-xs mb-3">Check columns to display. Uncheck to hide.</p>
                     {columns.map(col => (
                         <label key={col.id} className="modal-column-item flex items-center gap-3 px-3 py-2 rounded border border-transparent cursor-pointer transition-all">
                             <input
                                type="checkbox"
                                checked={!col.hidden}
                                onChange={() => onToggleColumnVisibility(col.id)}
                                className="modal-checkbox w-4 h-4 rounded"
                             />
                             <div className="flex flex-col">
                                 <span className={`modal-column-label text-sm font-medium ${col.hidden ? 'hidden-col' : ''}`}>{col.label}</span>
                                 <span className="modal-column-type text-[10px] capitalize">{col.type}</span>
                             </div>
                         </label>
                     ))}
                 </div>
             )}
          </div>

          <div className="modal-footer p-4 border-t flex justify-end gap-2 rounded-b-lg">
              {activeTab === 'rows' ? (
                  <>
                      <button onClick={onClose} className="modal-btn-secondary px-4 py-2 text-sm rounded">Cancel</button>
                      <button onClick={() => { setConditions([]); onApply(undefined); onDateRangeFilterChange(null); onClose(); }} className="modal-btn-danger px-4 py-2 text-sm rounded">Clear Filters</button>
                      <button onClick={handleApply} className="modal-btn-primary px-4 py-2 text-sm rounded font-medium shadow-sm">Apply Filter</button>
                  </>
              ) : (
                  <button onClick={onClose} className="modal-btn-primary px-4 py-2 text-sm rounded font-medium shadow-sm">Done</button>
              )}
          </div>
       </div>
    </div>
  );
};